import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Send, ExternalLink, Loader2, Search, Pencil, AlertTriangle, CheckCircle2, MapPin } from "lucide-react";
import AdminLayout from "./AdminLayout";

// ── Constants ──────────────────────────────────────────────────────────────

const DISTRICTS = [
  "中西區","灣仔區","東區","南區",
  "油尖旺區","深水埗區","九龍城區","黃大仙區","觀塘區",
  "葵青區","荃灣區","屯門區","元朗區",
  "北區","大埔區","沙田區","西貢區","離島區",
];

const CATEGORIES = [
  { slug: "catering",              label: "餐飲服務" },
  { slug: "engineering",           label: "工程維修" },
  { slug: "it",                    label: "資訊科技" },
  { slug: "school-bus",            label: "校車服務" },
  { slug: "uniform",               label: "校服" },
  { slug: "printing",              label: "印刷" },
  { slug: "cleaning",              label: "清潔服務" },
  { slug: "professional-services", label: "專業服務" },
  { slug: "study-tour",            label: "遊學" },
  { slug: "elevator",              label: "升降機工程" },
  { slug: "security",              label: "保安服務" },
  { slug: "gardening",             label: "園藝服務" },
  { slug: "fire-safety",           label: "消防" },
  { slug: "air-conditioning",      label: "冷氣" },
  { slug: "others",                label: "其他" },
];

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  raw:       { label: "原始",   cls: "bg-gray-100 text-gray-600 border-gray-300" },
  analyzed:  { label: "已分析", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  published: { label: "已發布", cls: "bg-green-50 text-green-700 border-green-300" },
};

// ── Types ──────────────────────────────────────────────────────────────────

interface SchoolLink {
  id: number;
  schoolNo: string;
  schoolName: string | null;
  schoolUrl: string | null;
  tenderPageUrl: string | null;
  tenderTitle: string | null;
  deadline: string | null;
  rawDeadline: string | null;
  scrapedAt: string | null;
  status: string;
  district: string | null;
  categorySlug: string | null;   // stored as JSON array string e.g. '["catering","it"]'
  notes: string | null;
  publishedTenderId: number | null;
}

interface EditForm {
  schoolNo: string;
  schoolName: string;
  schoolUrl: string;
  tenderPageUrl: string;
  tenderTitle: string;
  deadline: string;
  rawDeadline: string;
  status: string;
  district: string;
  categorySlugs: string[];
  notes: string;
}

interface PublishForm {
  title: string;
  district: string;
  categorySlugs: string[];
}

interface SchoolResult {
  id: number;
  name: string;
  district: string;
  address: string | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

async function adminFetch(path: string, init?: RequestInit, token?: string | null) {
  const res = await fetch(path, {
    ...init,
    headers: { ...(init?.headers ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

function parseSlugs(raw: string | null): string[] {
  if (!raw) return [];
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : [v]; }
  catch { return raw ? [raw] : []; }
}

function toEditForm(l: SchoolLink): EditForm {
  return {
    schoolNo:      l.schoolNo ?? "",
    schoolName:    l.schoolName ?? "",
    schoolUrl:     l.schoolUrl ?? "",
    tenderPageUrl: l.tenderPageUrl ?? "",
    tenderTitle:   l.tenderTitle ?? "",
    deadline:      l.deadline ? l.deadline.slice(0, 10) : "",
    rawDeadline:   l.rawDeadline ?? "",
    status:        l.status,
    district:      l.district ?? "",
    categorySlugs: parseSlugs(l.categorySlug),
    notes:         l.notes ?? "",
  };
}

function getMissingFields(l: SchoolLink): string[] {
  const m: string[] = [];
  if (!l.tenderTitle) m.push("招標標題");
  if (!l.district)    m.push("地區");
  if (!l.rawDeadline && !l.deadline) m.push("截止日期");
  return m;
}

function categoryLabels(raw: string | null): string[] {
  return parseSlugs(raw).map(s => CATEGORIES.find(c => c.slug === s)?.label ?? s);
}

// ── Sub-components ─────────────────────────────────────────────────────────

function DistrictGrid({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {DISTRICTS.map(d => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(value === d ? "" : d)}
          className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
            value === d
              ? "bg-primary text-primary-foreground border-primary font-medium"
              : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
          }`}
        >
          {d}
        </button>
      ))}
    </div>
  );
}

function CategoryMultiSelect({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const toggle = (slug: string) =>
    onChange(values.includes(slug) ? values.filter(s => s !== slug) : [...values, slug]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {CATEGORIES.map(c => (
        <button
          key={c.slug}
          type="button"
          onClick={() => toggle(c.slug)}
          className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
            values.includes(c.slug)
              ? "bg-primary text-primary-foreground border-primary font-medium"
              : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
          }`}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

// ── Debounced school search ────────────────────────────────────────────────

function useSchoolSearch(q: string) {
  const [dq, setDq] = useState(q);
  const { getToken } = useAuth();
  useEffect(() => { const t = setTimeout(() => setDq(q), 350); return () => clearTimeout(t); }, [q]);
  return useQuery<SchoolResult[]>({
    queryKey: ["school-search", dq],
    queryFn: async () => adminFetch(`/api/admin/schools/search?q=${encodeURIComponent(dq)}`, undefined, await getToken()),
    enabled: dq.length >= 2,
    staleTime: 30000,
  });
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function AdminSchoolsLinksPage() {
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const af = async (path: string, init?: RequestInit) => adminFetch(path, init, await getToken());
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const [editTarget, setEditTarget] = useState<SchoolLink | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [schoolSearch, setSchoolSearch] = useState("");
  const [selectedSchool, setSelectedSchool] = useState<SchoolResult | null>(null);

  const [publishTarget, setPublishTarget] = useState<SchoolLink | null>(null);
  const [publishForm, setPublishForm] = useState<PublishForm>({ title: "", district: "", categorySlugs: [] });

  const schoolResults = useSchoolSearch(schoolSearch);

  const { data: links = [], isLoading } = useQuery<SchoolLink[]>({
    queryKey: ["admin-schools-links"],
    queryFn: () => af("/api/admin/schools-links"),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      af(`/api/admin/schools-links/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      }),
    onMutate: ({ id }) => setUpdatingId(id),
    onSettled: () => setUpdatingId(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-schools-links"] }),
  });

  const updateRecord = useMutation({
    mutationFn: ({ id, form }: { id: number; form: EditForm }) =>
      af(`/api/admin/schools-links/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, categorySlugs: form.categorySlugs }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-schools-links"] });
      setEditTarget(null); setEditForm(null); setSchoolSearch(""); setSelectedSchool(null);
    },
  });

  const publish = useMutation({
    mutationFn: ({ id, form }: { id: number; form: PublishForm }) =>
      af(`/api/admin/schools-links/${id}/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ district: form.district, categorySlugs: form.categorySlugs, title: form.title || null }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-schools-links"] });
      setPublishTarget(null);
    },
  });

  function openEdit(l: SchoolLink) {
    setEditTarget(l); setEditForm(toEditForm(l));
    setSchoolSearch(l.schoolName ?? ""); setSelectedSchool(null);
  }

  function applySchoolResult(s: SchoolResult) {
    setSelectedSchool(s);
    setEditForm(f => f ? { ...f, district: s.district } : f);
  }

  function setField<K extends keyof EditForm>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setEditForm(f => f ? { ...f, [key]: e.target.value } : f);
  }

  function openPublish(l: SchoolLink, e: React.MouseEvent) {
    e.stopPropagation();
    setPublishTarget(l);
    setPublishForm({ title: l.tenderTitle ?? "", district: l.district ?? "", categorySlugs: parseSlugs(l.categorySlug) });
  }

  const counts = {
    all: links.length,
    raw: links.filter(l => l.status === "raw").length,
    analyzed: links.filter(l => l.status === "analyzed").length,
    published: links.filter(l => l.status === "published").length,
  };

  const filtered = links.filter(l => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.schoolName ?? "").toLowerCase().includes(q)
          || (l.tenderTitle ?? "").toLowerCase().includes(q)
          || (l.schoolNo ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  const incompleteCount = links.filter(l => getMissingFields(l).length > 0 && l.status !== "published").length;

  return (
    <AdminLayout tab="schools-links">
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-col gap-5">

          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold">來源連結管理</h2>
              <p className="text-sm text-muted-foreground mt-0.5">點擊任意行可編輯資料，完成分析後發布至招標數據庫</p>
            </div>
            {incompleteCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                <span>{incompleteCount} 筆資料不齊全，請先填寫地區和標題</span>
              </div>
            )}
          </div>

          {/* Filter + search */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-1.5 flex-wrap">
              {(["all", "raw", "analyzed", "published"] as const).map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    statusFilter === s
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {s === "all" ? "全部" : STATUS_MAP[s]?.label}
                  <span className="ml-1.5 opacity-70">({counts[s]})</span>
                </button>
              ))}
            </div>
            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="搜尋學校 / 標題..." className="pl-8 h-8 text-sm w-56" />
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" /> 載入中...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-sm border rounded-lg border-dashed">暫無記錄</div>
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/40">
                      <TableHead className="w-12">ID</TableHead>
                      <TableHead className="w-20">School No</TableHead>
                      <TableHead>學校名稱</TableHead>
                      <TableHead>招標標題</TableHead>
                      <TableHead className="w-20">地區</TableHead>
                      <TableHead className="w-28">分類</TableHead>
                      <TableHead className="w-28">截止日期</TableHead>
                      <TableHead className="w-14">連結</TableHead>
                      <TableHead className="w-32">狀態</TableHead>
                      <TableHead className="w-24 text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(l => {
                      const st = STATUS_MAP[l.status] ?? STATUS_MAP.raw;
                      const isUpdating = updatingId === l.id;
                      const missing = getMissingFields(l);
                      const hasIssue = missing.length > 0 && l.status !== "published";
                      const isLinked = !!l.publishedTenderId;
                      const cats = categoryLabels(l.categorySlug);

                      return (
                        <TableRow key={l.id}
                          className={`cursor-pointer hover:bg-yellow-50 transition-colors ${l.status === "published" && isLinked ? "opacity-55" : ""}`}
                          onClick={() => openEdit(l)}
                        >
                          <TableCell className="text-xs text-muted-foreground">{l.id}</TableCell>
                          <TableCell className="text-xs font-mono">{l.schoolNo || "—"}</TableCell>
                          <TableCell className="font-medium text-sm max-w-[140px]">
                            <span className="line-clamp-1">{l.schoolName || <span className="text-muted-foreground/40">—</span>}</span>
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px]">
                            <div className="flex items-start gap-1.5">
                              {hasIssue && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">缺少：{missing.join("、")}</TooltipContent>
                                </Tooltip>
                              )}
                              <span className="line-clamp-2">{l.tenderTitle || <span className="text-muted-foreground/40 italic">未填寫</span>}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{l.district || "—"}</TableCell>
                          <TableCell>
                            {cats.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {cats.map(c => (
                                  <span key={c} className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[11px]">{c}</span>
                                ))}
                              </div>
                            ) : <span className="text-muted-foreground/40 text-xs">—</span>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {l.rawDeadline || (l.deadline ? l.deadline.slice(0, 10) : "—")}
                          </TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <div className="flex gap-0.5">
                              {l.tenderPageUrl && (
                                <a href={l.tenderPageUrl} target="_blank" rel="noreferrer" title="招標頁面">
                                  <Button size="icon" variant="ghost" className="h-6 w-6"><ExternalLink className="h-3 w-3" /></Button>
                                </a>
                              )}
                              {l.schoolUrl && (
                                <a href={l.schoolUrl} target="_blank" rel="noreferrer" title="學校網站">
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground"><ExternalLink className="h-3 w-3" /></Button>
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Select value={l.status} onValueChange={status => updateStatus.mutate({ id: l.id, status })} disabled={isUpdating}>
                              <SelectTrigger className={`h-7 text-xs border ${st.cls} w-28`}>
                                {isUpdating ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue />}
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="raw">原始</SelectItem>
                                <SelectItem value="analyzed">已分析</SelectItem>
                                <SelectItem value="published">已發布</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {isLinked && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-0.5 text-xs text-green-600 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">
                                      <CheckCircle2 className="h-3 w-3" />#{l.publishedTenderId}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">已建立招標 #{l.publishedTenderId}</TooltipContent>
                                </Tooltip>
                              )}
                              <Button size="icon" variant="ghost"
                                className="h-7 w-7 text-muted-foreground hover:text-amber-500 hover:bg-amber-50"
                                title="編輯" onClick={() => openEdit(l)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {!isLinked && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button size="icon" variant="ghost"
                                      className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={e => openPublish(l, e)}
                                    >
                                      <Send className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    {hasIssue ? `資料不齊 (缺 ${missing.join("、")})，仍可發布` : "發布至招標管理"}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        {/* ── Edit Dialog ── */}
        <Dialog open={!!editTarget} onOpenChange={o => { if (!o) { setEditTarget(null); setEditForm(null); setSchoolSearch(""); setSelectedSchool(null); } }}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>編輯記錄 #{editTarget?.id}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">修改後點擊「儲存」即時更新數據庫</DialogDescription>
            </DialogHeader>
            {editForm && (
              <div className="grid gap-4 py-1">

                {/* School No + Status */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">School No</Label>
                    <Input value={editForm.schoolNo} onChange={setField("schoolNo")} className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">狀態</Label>
                    <Select value={editForm.status} onValueChange={v => setEditForm(f => f ? { ...f, status: v } : f)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="raw">原始</SelectItem>
                        <SelectItem value="analyzed">已分析</SelectItem>
                        <SelectItem value="published">已發布</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* School name */}
                <div className="space-y-1">
                  <Label className="text-xs">學校名稱</Label>
                  <Input
                    value={editForm.schoolName}
                    onChange={e => { setField("schoolName")(e); setSchoolSearch(e.target.value); setSelectedSchool(null); }}
                    className="h-8 text-sm" placeholder="例：聖保羅書院"
                  />
                </div>

                {/* School lookup results */}
                {schoolSearch.length >= 2 && (
                  <div className="rounded-lg border bg-muted/30 p-2 space-y-1">
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1 mb-1.5">
                      <MapPin className="h-3 w-3" /> 從學校資料庫匹配（點擊自動填入地區）
                    </p>
                    {schoolResults.isLoading ? (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-1 px-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> 搜尋中...
                      </div>
                    ) : (schoolResults.data ?? []).length === 0 ? (
                      <p className="text-xs text-muted-foreground px-1 py-1">找不到符合的學校</p>
                    ) : (
                      (schoolResults.data ?? []).map(s => (
                        <button key={s.id} onClick={() => applySchoolResult(s)}
                          className={`w-full text-left rounded px-2 py-1.5 text-xs transition-colors ${
                            selectedSchool?.id === s.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                          }`}
                        >
                          <span className="font-medium">{s.name}</span>
                          <span className="mx-1.5 opacity-50">·</span>
                          <span>{s.district}</span>
                          {s.address && <span className="block opacity-60 truncate mt-0.5">{s.address}</span>}
                        </button>
                      ))
                    )}
                  </div>
                )}

                {/* Selected school confirmed */}
                {selectedSchool && (
                  <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-medium">已填入地區：{selectedSchool.district}</span>
                      {selectedSchool.address && <div className="opacity-70 mt-0.5">地址：{selectedSchool.address}</div>}
                    </div>
                  </div>
                )}

                {/* Tender title */}
                <div className="space-y-1">
                  <Label className="text-xs">
                    招標標題
                    {!editForm.tenderTitle && <span className="ml-1 text-amber-500 text-[10px]">（未填寫，發布時必填）</span>}
                  </Label>
                  <Input value={editForm.tenderTitle} onChange={setField("tenderTitle")} className="h-8 text-sm" placeholder="例：2025-26年度午膳服務招標" />
                </div>

                {/* District grid */}
                <div className="space-y-2">
                  <Label className="text-xs">
                    地區
                    {!editForm.district && <span className="ml-1 text-amber-500 text-[10px]">（未選擇，發布時必填）</span>}
                    {editForm.district && <span className="ml-1.5 text-primary font-medium">{editForm.district}</span>}
                  </Label>
                  <DistrictGrid
                    value={editForm.district}
                    onChange={v => setEditForm(f => f ? { ...f, district: v } : f)}
                  />
                </div>

                {/* Category multi-select */}
                <div className="space-y-2">
                  <Label className="text-xs">
                    行業分類（可多選）
                    {editForm.categorySlugs.length > 0 && (
                      <span className="ml-1.5 text-primary font-medium">
                        已選 {editForm.categorySlugs.length} 項
                      </span>
                    )}
                  </Label>
                  <CategoryMultiSelect
                    values={editForm.categorySlugs}
                    onChange={v => setEditForm(f => f ? { ...f, categorySlugs: v } : f)}
                  />
                </div>

                {/* Deadline */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">截止日期（原文）</Label>
                    <Input value={editForm.rawDeadline} onChange={setField("rawDeadline")} className="h-8 text-sm" placeholder="例：2025年5月31日" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">截止日期（標準）</Label>
                    <Input type="date" value={editForm.deadline} onChange={setField("deadline")} className="h-8 text-sm" />
                  </div>
                </div>

                {/* URLs */}
                <div className="space-y-1">
                  <Label className="text-xs">招標頁面 URL</Label>
                  <Input value={editForm.tenderPageUrl} onChange={setField("tenderPageUrl")} className="h-8 text-sm font-mono text-xs" placeholder="https://..." />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">學校網站 URL</Label>
                  <Input value={editForm.schoolUrl} onChange={setField("schoolUrl")} className="h-8 text-sm font-mono text-xs" placeholder="https://..." />
                </div>

                {/* Notes */}
                <div className="space-y-1">
                  <Label className="text-xs">備註</Label>
                  <Textarea
                    value={editForm.notes}
                    onChange={e => setEditForm(f => f ? { ...f, notes: e.target.value } : f)}
                    placeholder="內部備註，不會公開顯示..." className="text-sm resize-none" rows={3}
                  />
                </div>

              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setEditTarget(null); setEditForm(null); setSchoolSearch(""); setSelectedSchool(null); }}>取消</Button>
              <Button
                disabled={updateRecord.isPending}
                onClick={() => editTarget && editForm && updateRecord.mutate({ id: editTarget.id, form: editForm })}
              >
                {updateRecord.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                儲存
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Publish Dialog ── */}
        <Dialog open={!!publishTarget} onOpenChange={o => !o && setPublishTarget(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>發布至招標管理</DialogTitle>
              <DialogDescription className="text-xs">確認資料後發布，將建立一筆新招標記錄</DialogDescription>
            </DialogHeader>
            {publishTarget && (() => {
              const missing = getMissingFields(publishTarget);
              return (
                <div className="space-y-4 py-2">
                  {missing.length > 0 && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>部分資料未填寫（{missing.join("、")}），請在下方補充後再發布。</span>
                    </div>
                  )}

                  {/* Title */}
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">招標標題 <span className="text-destructive">*</span></Label>
                    <Input
                      value={publishForm.title}
                      onChange={e => setPublishForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="輸入招標標題..." className="text-sm"
                    />
                  </div>

                  {/* District grid */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      地區 <span className="text-destructive">*</span>
                      {publishForm.district && <span className="ml-2 text-primary font-medium text-sm">{publishForm.district}</span>}
                    </Label>
                    <DistrictGrid
                      value={publishForm.district}
                      onChange={v => setPublishForm(f => ({ ...f, district: v }))}
                    />
                  </div>

                  {/* Category multi-select */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      行業分類（可多選）
                      {publishForm.categorySlugs.length > 0 && (
                        <span className="ml-2 text-primary font-medium text-sm">已選 {publishForm.categorySlugs.length} 項</span>
                      )}
                    </Label>
                    <CategoryMultiSelect
                      values={publishForm.categorySlugs}
                      onChange={v => setPublishForm(f => ({ ...f, categorySlugs: v }))}
                    />
                  </div>

                  {/* Preview */}
                  <div className="rounded-lg bg-secondary/40 p-3 space-y-1 text-xs text-muted-foreground">
                    <div className="font-medium text-foreground text-sm mb-1.5">發布預覽</div>
                    <div><span className="w-16 inline-block">學校</span>{publishTarget.schoolName || "—"}</div>
                    <div><span className="w-16 inline-block">截止</span>{publishTarget.rawDeadline || publishTarget.deadline?.slice(0, 10) || "—"}</div>
                    <div><span className="w-16 inline-block">連結</span>
                      {publishTarget.tenderPageUrl
                        ? <a href={publishTarget.tenderPageUrl} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">查看原始頁面</a>
                        : "—"}
                    </div>
                  </div>
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPublishTarget(null)}>取消</Button>
              <Button
                disabled={!publishForm.title || !publishForm.district || publish.isPending}
                onClick={() => publishTarget && publish.mutate({ id: publishTarget.id, form: publishForm })}
              >
                {publish.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                確認發布
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TooltipProvider>
    </AdminLayout>
  );
}
